/**
 * WHAT HAPPENED, READ OFF TWO SNAPSHOTS OF THE SAVE.
 *
 * -- WHY IT IS A DIFF AND NOT A CALLBACK -----------------------------------------
 *
 * The simulation has no event bus and it must not grow one for this. CONTRACT section 6:
 * presence changes nothing, and the whole lane rests on a state that is a pure function of
 * elapsed time - a save settled offline lands exactly what a watched tab lands, and it does it
 * without anything being called. Hooking a callback into `stepTick` would give the renderer a
 * route to observe ticks that a settle does not fire, which is the shape of a presence
 * violation even if this particular caller only made a noise with it.
 *
 * So sound reads the same thing the picture reads: the state, twice, and the difference. That
 * is already the mechanism the catch mark on the water uses (`noteCatches` in world-entry.ts),
 * and it is deliberately the mechanism here too, for the same reason and with the same limits.
 *
 * -- THE LIMIT THAT MATTERS, STATED RATHER THAN DISCOVERED -----------------------
 *
 * A diff cannot see an event that a later event has already covered over. Two landings between
 * two looks are one delta of two, and this file plays ONE sound for them and reports the
 * species with the largest share. That is correct behaviour rather than a compromise: at five
 * looks a second and a landing every four seconds at the very fastest, two-in-a-look is already
 * rare, and when it does happen firing twice would be worse than firing once.
 *
 * -- AND THE ONE IT SHARES WITH THE CATCH MARK -----------------------------------
 *
 * More than SETTLE_BURST_MAX landings between two looks means the save settled while nobody was
 * there. Those are silent. The mark on the water already works this way and calls itself a
 * witness rather than a receipt; a sound has more claim on a room than a mark on water does, so
 * it keeps the same rule with no exceptions.
 */

import { SPECIES } from '../sim/index';
import { SETTLE_BURST_MAX } from './spec';

/** The only fields of the save this lane reads. Nothing here is written, ever. */
export interface AudioSnapshot {
  readonly catches: Readonly<Record<string, number>>;
  readonly records: Readonly<Record<string, number>>;
  readonly weightG: number;
  readonly junk: number;
  readonly specials: number;
}

/** One thing to make a sound about. */
export type AudioEvent =
  | {
      readonly kind: 'land';
      readonly species: string;
      /** 0 for the smallest of its species, 1 for the largest. Drives how heavy it sounds. */
      readonly weightFraction: number;
      /** A stable string for this landing, so the variant chosen for it is repeatable. */
      readonly token: string;
    }
  | { readonly kind: 'record'; readonly species: string; readonly token: string }
  | { readonly kind: 'special'; readonly token: string }
  | { readonly kind: 'junk'; readonly token: string };

/** Take the fields this lane needs off anything shaped like a state. */
export function snapshotOf(state: AudioSnapshot): AudioSnapshot {
  return {
    catches: { ...state.catches },
    records: { ...state.records },
    weightG: state.weightG,
    junk: state.junk,
    specials: state.specials,
  };
}

const speciesById = new Map(SPECIES.map((s) => [s.id, s]));

/**
 * Where a fish of `grams` falls in its own species range, 0 to 1.
 *
 * PER SPECIES, not across the table, and that is the whole point of the number. A three-kilo
 * cod is an ordinary cod and a three-kilo mackerel does not exist; a landing that got louder
 * with absolute weight would make every mackerel sound like a disappointment forever, which is
 * the opposite of what CONTRACT section 1.5l(a) asks mastery to do for the species you chose.
 * An unknown species id - which a save from a later build can legitimately carry - lands in the
 * middle rather than at an end.
 */
export function weightFractionOf(speciesId: string, grams: number): number {
  const s = speciesById.get(speciesId);
  if (s === undefined) return 0.5;
  const span = s.maxWeightG - s.minWeightG;
  if (span <= 0) return 0.5;
  return Math.max(0, Math.min(1, (grams - s.minWeightG) / span));
}

/**
 * Everything that happened between two looks, in the order it should be heard.
 *
 * A personal best emits BOTH a landing and a record: the fish still came out of the water, and
 * a chime on its own would read as an achievement notification rather than as a big fish. The
 * engine spaces the two.
 */
export function eventsBetween(before: AudioSnapshot, after: AudioSnapshot): AudioEvent[] {
  const out: AudioEvent[] = [];

  let landings = 0;
  let best: string | null = null;
  let bestDelta = 0;
  for (const [id, n] of Object.entries(after.catches)) {
    const d = n - (before.catches[id] ?? 0);
    if (d <= 0) continue;
    landings += d;
    if (d > bestDelta) {
      bestDelta = d;
      best = id;
    }
  }

  // A settle, not a session. Silent, and the junk and specials that came with it are silent too.
  if (landings > SETTLE_BURST_MAX) return out;

  if (best !== null && landings > 0) {
    const grams = (after.weightG - before.weightG) / landings;
    const token = `${best}:${after.catches[best] ?? 0}`;
    out.push({ kind: 'land', species: best, weightFraction: weightFractionOf(best, grams), token });
    const wasRecord = before.records[best] ?? 0;
    const isRecord = after.records[best] ?? 0;
    // A record only counts when there WAS a previous one. The first of a species sets a record
    // by definition and ringing for it would ring on every species the player ever meets.
    if (isRecord > wasRecord && wasRecord > 0) {
      out.push({ kind: 'record', species: best, token: `${token}:pb` });
    }
  }

  const specialDelta = after.specials - before.specials;
  if (specialDelta > 0 && specialDelta <= SETTLE_BURST_MAX) {
    out.push({ kind: 'special', token: `special:${after.specials}` });
  }

  const junkDelta = after.junk - before.junk;
  if (junkDelta > 0 && junkDelta <= SETTLE_BURST_MAX && landings === 0) {
    // Only when nothing was landed in the same look. A landing and a lump of weed in the same
    // 200 ms is one event to a listener, and the fish is the one worth hearing.
    out.push({ kind: 'junk', token: `junk:${after.junk}` });
  }

  return out;
}
